import { Tooltip, Typography } from "@mui/material";
import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Icon } from "../../../common/components";
import Avatar from "../../../common/components/Avatar/Avatar";
import DeleteModal from "../../../components/DeleteModal/DeleteModal";
import TeamService from "../../../service/TeamService";
import { palette, spacing, Styles } from "../../../styles/theme";
import { ROUTES } from "../../../constant/common.constant";
import { useTeam } from "../../../context/Team";
import { getInitials } from "../../../utils/initialsUtils";

const styles: Styles = {
  container: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
};

interface Props {
  userId: string;
  canDelete: boolean;
  isCurrentUser: boolean;
  first_name: string;
  last_name: string;
  email: string;
  teamId: string;
  teammateUserId: string;
  imageUrl?: string;
  isInternalTeamMember: boolean;
}

const Teammate = (props: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    userId,
    canDelete,
    isCurrentUser,
    first_name,
    last_name,
    email,
    teamId,
    teammateUserId,
    imageUrl,
    isInternalTeamMember,
  } = props;

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { refetchTeam, refetchTeams } = useTeam();

  const onDeleteClickClose = () => {
    setShowDeleteModal(false);
  };

  const onDelete = useCallback(async () => {
    try {
      await TeamService.removeTeammate({
        teamId: teamId,
        teammateId: teammateUserId,
      });
      //If we just removed ourselves from a team, then we must redirect to general page
      if (isCurrentUser) {
        navigate(ROUTES.HOME);
        refetchTeams({ skipLoading: true });
      } else {
        await refetchTeam({ skipLoading: true });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setShowDeleteModal(false);
    }
  }, [
    teamId,
    teammateUserId,
    refetchTeam,
    refetchTeams,
    navigate,
    isCurrentUser,
  ]);

  const initials = useMemo(
    () => getInitials(first_name, last_name),
    [first_name, last_name]
  );

  return (
    <Box sx={styles.container}>
      <Box display="flex" alignItems="center" gap={1.25} flexGrow={1}>
        <Avatar
          initials={initials}
          userId={userId}
          imageUrl={imageUrl}
          size="lg"
          noBorder
        />

        <Box display="flex" flexDirection="column" maxWidth={"205px"}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: spacing.xxsDesktop,
            }}
          >
            <Typography
              variant="textRegular"
              textOverflow={"ellipsis"}
              whiteSpace={"nowrap"}
              overflow={"hidden"}
              display={"block"}
            >
              {`${first_name} ${last_name}`}
            </Typography>
            {isInternalTeamMember && (
              <Tooltip title={t("team.jameoTeam")} placement="top" arrow>
                <Box>
                  <Icon
                    icon="new_releases"
                    color={palette.brand.secondary}
                    size={12}
                    filled
                  />
                </Box>
              </Tooltip>
            )}
          </Box>
          <Typography
            variant="textSm"
            textOverflow={"ellipsis"}
            whiteSpace={"nowrap"}
            overflow={"hidden"}
            display={"block"}
            color={palette.grey.primary}
          >
            {email}
          </Typography>
        </Box>
      </Box>
      <Box>
        {canDelete && (
          <Icon
            icon="close"
            size={20}
            onClick={() => {
              setShowDeleteModal(true);
            }}
          />
        )}
      </Box>
      <DeleteModal
        open={showDeleteModal}
        onDelete={onDelete}
        title={t("project.remove_teammate_title")}
        messageKey={"project.remove_teammate_modal"}
        messageParams={{ first_name, last_name }}
        ctaText={t("project.remove_teammate_cta", { first_name })}
        onClose={onDeleteClickClose}
      />
    </Box>
  );
};

export default memo(Teammate);
